import { Component, computed, DestroyRef, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DocumentRecord } from "@models/annual-statements/document-record";
import { EntityType } from '@models/common/entity-type.enum';
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { CompanyChangeOfficerType } from "@models/relationships/company-change-officer-type";
import { Relationship } from "@models/relationships/relationship";
import { IDetailTab } from "@models/ui-models/detail-tab";
import { MenuSection, SubMenuItem } from "@models/ui-models/section-form-menu";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { IOfficeholdersPatchModel } from "@pages/companies/models/company-officeholders-state.interface";
import { IShareholdersPatchModel } from "@pages/companies/models/company-shareholders-state.interface";
import { OfficeholderGroupModel } from "@pages/companies/models/officeholder-group-model";
import { CompanyProfileExportService } from "@pages/companies/services/company-profile-export.service";
import { CompanyProfileStateService, } from "@pages/companies/services/company-profile-state.service";
import {
  DocumentsGridComponent
} from "@pages/documents/components/documents-page/components/documents-grid/documents-grid.component";
import { AlertsListComponent } from "@shared/components/alerts-list/alerts-list.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  EntityPendingChangesComponent
} from "@shared/components/entity-profiles/entity-pending-changes/entity-pending-changes.component";
import {
  EntityProfileTagsComponent
} from "@shared/components/entity-profiles/entity-profile-tags/entity-profile-tags.component";
import {
  EntitySecurityholdersViewComponent
} from "@shared/components/entity-profiles/entity-securityholders-view/entity-securityholders-view.component";
import {
  SecurityholderViewModel
} from "@shared/components/entity-profiles/entity-securityholders-view/securityholder-view-model";
import { NotesComponent } from "@shared/components/notes/notes.component";
import { TabButtonComponent } from "@shared/controls/tab-button/tab-button.component";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { getPageTitle } from "@shared/utils/get-page-title";
import { getQuantityLabel } from "@shared/utils/get-quantity-label";
import { Guid } from "@shared/utils/guid.helper";
import {
  CompanyChangeSpecialPurpose
} from "../../../../forms/asic-forms/company/company-change-special-purpose/company-change-special-purpose.model";
import {
  CompanyChangeAddress,
  CompanyChangeDeregister,
  CompanyChangeHoldingCompany,
  CompanyChangeName,
  CompanyChangeOfficer,
  CompanyChangeOfficerResignation,
  CompanyChangePartyName,
  CompanyChangeType,
  CompanyForm361,
  CompanyForm388,
  CompanyMistakeCorrection,
  CompanyWithdrawLodgedDocumentChange,
  Form410BTransaction,
  Form410FTransaction
} from "../../../../forms/models/company";
import { CompanyMistakeCorrectionType } from "../../../../forms/models/company/company-mistake-correction.model";
import { CompanyDetailsComponent } from "./components/company-details/company-details.component";
import { CompanyOfficeholdersComponent } from "./components/company-officeholders/company-officeholders.component";
import { CompanyProfileCardComponent } from './components/company-profile-card/company-profile-card.component';

export type ICompanyProfileTabKeys = 'Details' | 'Officeholders' | 'Shareholders' | 'Documents';
export const companyProfileTabKeysList: ICompanyProfileTabKeys[] = ['Details', 'Officeholders', 'Shareholders', 'Documents'];

@Component({
  selector: 'app-company-profile',
  standalone: true,
  templateUrl: 'company-profile-page.component.html',
  styleUrl: 'company-profile-page.component.scss',
  imports: [
    CompanyDetailsComponent,
    CompanyProfileCardComponent,
    EntityProfileTagsComponent,
    EntityPendingChangesComponent,
    CompanyOfficeholdersComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    TabButtonComponent,
    NotesComponent,
    LoaderComponent,
    AlertsListComponent,
    HasRoleDirective,
    RouterLink,
    EntitySecurityholdersViewComponent,
    DocumentsGridComponent
  ],
  providers: [
    CompanyProfileExportService,
    CompanyProfileStateService
  ],
})
export class CompanyProfilePageComponent extends CommonPageComponent implements OnInit {
  companyOfficeholdersComponent = viewChild(CompanyOfficeholdersComponent);
  entitySecurityholdersComponent = viewChild(EntitySecurityholdersViewComponent);

  private companyProfileStateService = inject(CompanyProfileStateService);
  private companyProfileExportService = inject(CompanyProfileExportService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);
  private destroyRef = inject(DestroyRef);

  readonly EntityType = EntityType;

  isCompanyLoading = this.companyProfileStateService.state.loaders.isCompanyLoading;
  isShareholdersLoading = this.companyProfileStateService.state.loaders.isShareholdersLoading;
  isPendingDocumentsLoading = this.companyProfileStateService.state.loaders.isPendingDocumentsLoading;
  isPendingChangesVisible = this.companyProfileStateService.state.isPendingChangesVisible;
  isModalOpened = this.companyProfileStateService.isModalOpened;
  isControlsDisabled = computed(() => this.isLoading() || this.isModalOpened());

  companyWithPendingChanges = this.companyProfileStateService.state.companyWithPendingChanges;
  lodgedCompany = this.companyProfileStateService.state.lodgedCompany;

  companyDetailsViewModel = this.companyProfileStateService.companyDetailsViewModel;

  pendingDocuments = this.companyProfileStateService.state.pendingDocumentRecords;

  officeholdersViewModel = this.companyProfileStateService.officeholdersViewModel;

  shareholdersViewModel = this.companyProfileStateService.shareholdersViewModel;

  notesTotal = this.companyProfileStateService.state.notesTotal;
  isXpmConnected = this.companyProfileStateService.state.isXpmConnected;
  isCurrentChangesHighlighted = this.companyProfileStateService.state.isCurrentChangesHighlighted;

  activeTab = signal<ICompanyProfileTabKeys>('Details');
  isTabsHidden = signal<boolean>(false);

  // Notes
  notesVisible = signal<boolean>(false);
  notesLabel = computed(() => getQuantityLabel(this.notesTotal()));

  // Alerts
  alertsVisible = signal<boolean>(false);
  alertsLabel = computed(() => getQuantityLabel(this.lodgedCompany().alerts.length, 'Alert'));

  companyDetailsActions: MenuSection[] = this.getCompanyDetailsActions();
  officeholdersMenu: SubMenuItem[] = this.getOfficeholdersMenuActions();
  shareholdersMenu: SubMenuItem[] = this.getShareholdersMenuActions();
  documentsMenu: SubMenuItem[] = this.getDocumentsMenuActions();
  tabs: IDetailTab<ICompanyProfileTabKeys>[] = [
    { title: 'Details' },
    { title: 'Officeholders' },
    { title: 'Shareholders' },
    { title: 'Documents' },
  ];
  selectedOfficeholders: Relationship[] = [];
  selectedShareholdersViewModels: SecurityholderViewModel[] = [];
  selectedDocuments: DocumentRecord[] = [];

  constructor() {
    super();

    effect(() => {
      const companyName = this.lodgedCompany().name;

      if (companyName) {
        this.titleService.setTitle(getPageTitle(companyName));
        this.headerService.setHeaderData({
          title: companyName,
          subtitle: 'Company',
          iconClass: 'ic-companies-20',
        });
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    const companyId = this.route.snapshot.paramMap.get('id');

    if (!companyId || !Guid.isValidGuid(companyId)) {
      void this.router.navigate(['/companies']);
      return;
    }

    this.companyProfileStateService.init(companyId);
    this.headerService.setHeaderLoading();

    this.listenOpenFormQueryParam();
    this.listenActiveTabQueryParam();
  }

  setIsTabsHidden(isTabsHidden: boolean) {
    this.isTabsHidden.set(isTabsHidden);
  }

  setIsPendingChangesVisible(isPendingChangesVisible: boolean) {
    this.companyProfileStateService.setIsPendingChangesVisible(isPendingChangesVisible);
  }

  onNotesTotalChanged(change: number): void {
    this.companyProfileStateService.setNotesTotal(this.notesTotal() + change);
  }

  // =====  Company Details  =====

  openEditCompanyDetailsModal(): void {
    this.companyProfileStateService.openEditCompanyDetailsModal();
  }

  editSigningBillingContact(): void {
    this.companyProfileStateService.openEditSigningBillingContact();
  }

  updateCompanyData() {
    this.companyProfileStateService.loadCompany();
    this.companyProfileStateService.loadPendingDocuments();
    this.setIsPendingChangesVisible(this.isPendingChangesVisible());
  }

  //for testing
  generateAnnualStatement(): void {
    this.companyProfileStateService.performGenerateFakeAnnualStatement();
  }

  // =====  Officeholders  =====

  patchOfficeholderViewModel(officeholdersPatchModel: IOfficeholdersPatchModel): void {
    this.companyProfileStateService.patchOfficeholderState(officeholdersPatchModel);
    this.setIsTabsHidden(!!officeholdersPatchModel.officeholderDetails);
  }

  onSelectOfficeholdersChange(offices: Relationship[] = []): void {
    this.selectedOfficeholders = offices;
  }

  get selectedOfficeholdersRelationshipIds(): string[] {
    return this.selectedOfficeholders.map((relationship) => relationship.relationshipId);
  }

  // =====  Shareholders  =====

  loadShareholders(): void {
    this.companyProfileStateService.loadShareholders();
  }

  patchShareholdersViewModel(shareholdersPatchModel: IShareholdersPatchModel): void {
    this.companyProfileStateService.patchShareholdersState(shareholdersPatchModel);
    this.setIsTabsHidden(!!shareholdersPatchModel.shareholderDetails);
  }

  onSelectShareholdersChange(models: SecurityholderViewModel[] = []): void {
    this.selectedShareholdersViewModels = models;
  }

  get selectedShareholdersRelationshipIds(): string[] {
    return this.selectedShareholdersViewModels.flatMap((model) => model.record.holders.map((holder) => holder.relationshipId));
  }

  // =====  Documents  =====

  deleteDocument(documentRecord: DocumentRecord): void {
    this.companyProfileStateService.tryDeleteDocuments([documentRecord]);
  }

  deleteSelectedDocuments(): void {
    this.companyProfileStateService.tryDeleteDocuments(this.selectedDocuments);
  }

  selectDocuments(documentRecords: DocumentRecord[]): void {
    this.selectedDocuments = documentRecords;
  }

  // =====  Query Params Related  =====

  static activeTabQueryParamKey = 'tab';

  listenOpenFormQueryParam(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['openForm'] === '492') {
        this.openCompanyMistakeCorrectionForm();
        this.clearQueryParams();
      }

      if (params['openModal'] === 'openBillingModal') {
        this.editSigningBillingContact();
        this.clearQueryParams();
      }
    });
  }

  listenActiveTabQueryParam(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          if (!this) return;

          this.setIsTabsHidden(false);

          const tab = params[CompanyProfilePageComponent.activeTabQueryParamKey] as ICompanyProfileTabKeys | null;

          if (!tab || !companyProfileTabKeysList.includes(tab)) {
            this.activeTab.set('Details');
            this.companyProfileStateService.onSwitchOnTab('Details');
            return;
          }

          this.activeTab.set(tab);
          this.companyProfileStateService.onSwitchOnTab(tab);
        },
        error: () => {
        }
      });
  }

  setActiveTab(tab: IDetailTab<ICompanyProfileTabKeys>): void {
    const queryTabName = tab.title != 'Details' ? tab.title : null;
    const queryParams = { [CompanyProfilePageComponent.activeTabQueryParamKey]: queryTabName };

    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' })
      .then(() => this.titleService.setTitle(getPageTitle(this.lodgedCompany().name)));
  }

  clearQueryParams(): void {
    void this.router.navigate([], { queryParams: {} });
  }

  // =====  Forms  =====

  openCompanyChangeAddressForm(relationshipIds: string[] = []) {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeAddress({ relationshipIds }));
  }

  openCompanyChangeNameForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeName());
  }

  openCompanyChangeHoldingCompanyForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeHoldingCompany());
  }

  openCompanyChangeSpecialPurposeForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeSpecialPurpose());
  }

  openCompanyChangeDeregisterForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeDeregister());
  }

  openCompanyForm361Form(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyForm361(), false, {}, {}, this.lodgedCompany());
  }

  openCompanyMistakeCorrectionForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyMistakeCorrection());
  }

  openCompanyNotificationOfAdditionalInformationForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyMistakeCorrection({ correctionType: CompanyMistakeCorrectionType.F902 }));
  }

  openCompanyWithdrawLodgedDocumentChangeForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyWithdrawLodgedDocumentChange());
  }

  openForm410FTransactionForm(): void {
    this.companyProfileStateService.openModalWithCompany(new Form410FTransaction());
  }

  openForm410BTransactionForm(): void {
    this.companyProfileStateService.openModalWithCompany(new Form410BTransaction());
  }

  openCompanyChangeOfficerAppointForm(offices: Relationship[] = []): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeOfficer({ offices: offices.filter(Boolean) }));
  }

  openCompanyChangeOfficerResignationForm(): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeOfficerResignation());
  }

  openCompanyChangePartyNameForm(relationshipIds: string[] = []): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangePartyName({ relationshipIds }));
  }

  openCompanyChangeOfficerCeaseForm(offices: Relationship[] = []): void {
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeOfficer({
      actionType: CompanyChangeOfficerType.Removal,
      offices: offices.filter(Boolean)
    }));
  }

  openCompanyChangeTypeForm(): void {
    return;
    this.companyProfileStateService.openModalWithCompany(new CompanyChangeType());
  }

  openCompanyForm388Form(): void {
    return;
    this.companyProfileStateService.openModalWithCompany(new CompanyForm388());
  }

  openDraftForm(documentRecord: DocumentRecord): void {
    this.companyProfileStateService.openDraftForm(documentRecord);
  }

  // =====  Export  =====

  exportOfficeholders(exportType: ExportTypeEnum): void {
    if (!this.companyOfficeholdersComponent()) return;

    const allRows = (this.companyOfficeholdersComponent()?.officeholdersViewModel().officeholdersViewModel as OfficeholderGroupModel[]) ?? [];
    const onlySelected = this.selectedOfficeholders.length > 0;

    this.companyProfileExportService.exportOfficeholders(
      exportType,
      this.companyWithPendingChanges(),
      this.companyOfficeholdersComponent()!,
      allRows,
      onlySelected
    );
  }

  exportShareholders(exportType: ExportTypeEnum): void {
    if (!this.entitySecurityholdersComponent()) return;

    const shareholders: SecurityholderViewModel[] = this.selectedShareholdersViewModels.length
      ? this.selectedShareholdersViewModels
      : ((this.entitySecurityholdersComponent()?.shareholdersViewModel().shareholders as any[]) ?? []);
    const onlySelected = this.selectedShareholdersViewModels.length > 0;

    this.companyProfileExportService.exportShareholders(
      exportType,
      this.companyWithPendingChanges(),
      this.entitySecurityholdersComponent()!,
      shareholders,
      onlySelected
    );
  }

  // =====  Menu Items  =====


  getCompanyDetailsActions(): MenuSection[] {
    return [
      {
        title: 'Edit',
        subMenuItems: [
          {
            title: 'Edit Company Details',
            iconClass: 'ic-edit-company-20',
            action: () => this.openEditCompanyDetailsModal(),
          },
          {
            title: 'Generate Annual Statement (480F)',
            formName: 'Form 480F',
            iconClass: 'ic-annual-statement',
            action: () => void this.generateAnnualStatement(),
            roles: ['superuser'],
            classes: 'superuser'
          },
        ],
      },
      {
        title: '',
        subMenuItems: [
          {
            title: 'Edit Billing & Signing contact details',
            iconClass: 'ic-edit-billing-20',
            action: () => this.editSigningBillingContact(),
          },
        ],
      },
      {
        title: 'Company Name',
        subMenuItems: [
          {
            title: 'Company Name Change',
            formName: 'Form 205',
            iconClass: 'ic-company-name-change-20',
            action: () => this.openCompanyChangeNameForm(),
          },
          {
            title: 'Company Name Reservation',
            formName: 'Form 410B',
            iconClass: 'ic-reserve-new-company-name-20',
            action: () => this.openForm410BTransactionForm(),
          },
          {
            title: 'Extension of Company Name Reservation',
            formName: 'Form 410F',
            iconClass: 'ic-extend-company-name-reservation-20',
            action: () => this.openForm410FTransactionForm(),
          },
        ],
      },
      {
        title: 'Company Status',
        subMenuItems: [
          {
            title: 'Change of Company Status',
            formName: 'Form 484B3',
            iconClass: 'ic-change-to-company-status-20',
            action: () => this.openCompanyChangeSpecialPurposeForm(),
            disabled: this.companyWithPendingChanges().isPublic
          },
          // {
          //   title: 'Change to Company Type',
          //   formName: 'Form 206',
          //   action: () => this.openCompanyChangeTypeForm(),
          // },
          {
            title: 'Voluntary Deregistration',
            formName: 'Form 6010',
            iconClass: 'ic-voluntary-de-registration-20',
            action: () => this.openCompanyChangeDeregisterForm(),
          },
        ],
      },
      {
        title: 'Company Details Change',
        subMenuItems: [
          {
            title: 'Change of Address',
            formName: 'Form 484A1',
            iconClass: 'ic-change-address-20',
            action: () => this.openCompanyChangeAddressForm()
          },
          {
            title: 'Change of Ultimate Holding Company',
            formName: 'Form 484A3',
            iconClass: 'ic-change-of-ultimate-holding-company-20',
            action: () => this.openCompanyChangeHoldingCompanyForm(),
          },
        ],
      },
      {
        title: 'Others',
        subMenuItems: [
          // {
          //   title: 'Copy of Financial Statements and Reports',
          //   formName: 'Form 388',
          //   action: () => this.openCompanyForm388Form(),
          // },
          {
            title: 'Cease as a Registered Agent',
            formName: 'Form 361',
            iconClass: 'ic-registered-agent-ceasing-20',
            action: () => this.openCompanyForm361Form(),
          },
        ],
      },
      {
        title: 'Officers',
        subMenuItems: [
          {
            title: 'Notification by Officeholder of Resignation or Retirement',
            formName: 'Form 370',
            iconClass: 'ic-notification-by-officeholder-20',
            action: () => this.openCompanyChangeOfficerResignationForm(),
          },
          {
            title: 'Officer Name Change',
            formName: 'Form 484A2',
            iconClass: 'ic-change-officer-name-20',
            action: () => this.openCompanyChangePartyNameForm(this.selectedOfficeholdersRelationshipIds),
          },
        ],
      },
      {
        title: '',
        subMenuItems: [
          {
            title: 'Cessation of Officeholder',
            formName: 'Form 484B1',
            iconClass: 'ic-cessation-of-officeholder-20',
            action: () => this.openCompanyChangeOfficerCeaseForm(this.selectedOfficeholders),
          },
          {
            title: 'Appointment of Officeholder',
            formName: 'Form 484B2',
            iconClass: 'ic-appoint-officeholder-20',
            action: () => this.openCompanyChangeOfficerAppointForm(this.selectedOfficeholders),
          },
        ],
      },
    ];
  }

  getOfficeholdersMenuActions(): SubMenuItem[] {
    return [
      {
        title: 'Change of Address',
        formName: 'Form 484A1',
        iconClass: 'ic-change-address-20',
        action: () => this.openCompanyChangeAddressForm(this.selectedOfficeholdersRelationshipIds),
      },
      {
        title: 'Change Officer Name',
        formName: 'Form 484A2',
        iconClass: 'ic-change-officer-name-20',
        action: () => this.openCompanyChangePartyNameForm(this.selectedOfficeholdersRelationshipIds)
      },
      {
        title: 'Cessation of Officeholder',
        formName: 'Form 484B1',
        iconClass: 'ic-cessation-of-officeholder-20',
        action: () => this.openCompanyChangeOfficerCeaseForm(this.selectedOfficeholders),
      },
      {
        title: 'Appointment of Officeholder',
        formName: 'Form 484B2',
        iconClass: 'ic-appoint-officeholder-20',
        action: () => this.openCompanyChangeOfficerAppointForm(this.selectedOfficeholders),
      },
      {
        title: 'Notification by Officeholder of Resignation or Retirement',
        formName: 'Form 370',
        itemClass: 'dropdown-item-full-width',
        iconClass: 'ic-notification-by-officeholder-20',
        action: () => this.openCompanyChangeOfficerResignationForm(),
      },
    ];
  }

  getShareholdersMenuActions(): SubMenuItem[] {
    return [
      {
        title: 'Change Shareholder Name',
        formName: 'Form 484A2',
        iconClass: 'ic-change-officer-name-20',
        action: () => this.openCompanyChangePartyNameForm(this.selectedShareholdersRelationshipIds),
      },
      {
        title: 'Change of Address',
        formName: 'Form 484A1',
        iconClass: 'ic-change-address-20',
        action: () => this.openCompanyChangeAddressForm(this.selectedShareholdersRelationshipIds),
      },
    ];
  }

  getDocumentsMenuActions(): SubMenuItem[] {
    return [
      {
        title: 'Request for Correction',
        formName: 'Form 492',
        iconClass: 'ic-request-for-corrections-20',
        action: () => this.openCompanyMistakeCorrectionForm(),
      },
      {
        title: 'Notification of Supplementary Information',
        formName: 'Form 902',
        iconClass: 'ic-notification-of-additional-info-20',
        action: () => this.openCompanyNotificationOfAdditionalInformationForm(),
      },
      {
        title: 'Request to Withdraw a Lodged Document',
        formName: 'Form 106',
        iconClass: 'ic-withdraw-20',
        action: () => this.openCompanyWithdrawLodgedDocumentChangeForm(),
      },
    ];
  }
}

